import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactPaginate from "react-paginate";
import { captureEvent } from "~/helpers/capture_posthog_event";
import { apiFetch } from "~/lib/fetchClient";
import PromptsListErrorCard from "./PromptsListErrorCard";

const TRUNCATE_LENGTH = 80;

const promptTitle = (prompt) => {
  if (prompt.title) {
    return prompt.title;
  }
  if (prompt.body.length <= TRUNCATE_LENGTH) {
    return prompt.body;
  }
  return `${prompt.body.slice(0, TRUNCATE_LENGTH)}…`;
};

// Sub-2-char queries don't trigger a search — mirrors usePaginatedSearch's
// minSearchLength default so the library search behaves like the
// campaigns/labels search bars.
const MIN_SEARCH_LENGTH = 2;

// Deterministic key for the filter tuple. Sorting the channel array before
// joining avoids spurious mismatches when the parent memoises the array in
// different orders; plain field joins avoid JSON.stringify's object-key-order
// sensitivity (`{a,b}` vs `{b,a}` serialize to different strings).
const buildFilterKey = (categoryId, moduleTag, channels, search) =>
  [
    categoryId ?? "",
    moduleTag ?? "",
    channels ? [...channels].sort().join(",") : "",
    search ?? "",
  ].join("|");

const PromptsList = ({
  handlePromptClick,
  selectedCategory = null,
  onLoadingChange = undefined,
  moduleTag = null,
  channels = null,
  searchTerm = "",
}) => {
  const categoryId = selectedCategory?.value ?? null;
  // A debounced term shorter than MIN_SEARCH_LENGTH is treated as no search.
  const effectiveSearch =
    searchTerm.trim().length >= MIN_SEARCH_LENGTH ? searchTerm.trim() : "";

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [prompts, setPrompts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // Ref-backed stable access to the parent callback. Storing it in a ref
  // (instead of listing it in the useEffect deps) avoids re-triggering the
  // fetch whenever the parent re-renders with a fresh arrow function, while
  // still using the latest callback on each invocation.
  const onLoadingChangeRef = useRef(onLoadingChange);
  useEffect(() => {
    onLoadingChangeRef.current = onLoadingChange;
  }, [onLoadingChange]);

  // Reset to page 1 when filters change (PRD AC 2.2.1). Derived-state pattern
  // — page reset and filter change land in the same render, no extra fetch.
  const filterKey = buildFilterKey(
    categoryId,
    moduleTag,
    channels,
    effectiveSearch,
  );
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }

  // Monotonically-increasing request id — guards against out-of-order responses
  // when the user changes category/page mid-fetch and the browser resolves the
  // older request after the newer one.
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const params = { category: categoryId, page: currentPage };
    if (moduleTag) {
      params.module = moduleTag;
    }
    if (channels) {
      params.channel = channels;
    }
    if (effectiveSearch) {
      params.search = effectiveSearch;
    }

    const req = apiFetch.get("/ai_prompts", { params });

    setIsLoading(true);
    onLoadingChangeRef.current?.(true);

    (async () => {
      try {
        const body = await req;
        if (requestIdRef.current !== requestId) {
          return;
        }
        setPrompts(body.prompts || []);
        setTotalPages(body.pagination?.total_pages || 0);
        setHasError(false);
      } catch (err) {
        if (requestIdRef.current !== requestId) {
          return;
        }
        if (err?.name === "AbortError") {
          return;
        }
        setPrompts([]);
        setTotalPages(0);
        setHasError(true);
      } finally {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
          setIsRetrying(false);
          setIsInitialLoad(false);
          onLoadingChangeRef.current?.(false);
        }
      }
    })();

    return () => {
      req.abort();
    };
  }, [
    categoryId,
    currentPage,
    retryNonce,
    moduleTag,
    channels,
    effectiveSearch,
  ]);

  const handlePageChange = useCallback(({ selected }) => {
    setCurrentPage(selected + 1);
  }, []);

  const handleRetry = useCallback(() => {
    captureEvent("ai_assist_library_prompts_retry", {
      category_id: categoryId,
      module: moduleTag,
      channel: channels,
    });
    setIsRetrying(true);
    setRetryNonce((n) => n + 1);
  }, [categoryId, moduleTag, channels]);

  if (hasError) {
    return (
      <PromptsListErrorCard isRetrying={isRetrying} onRetry={handleRetry} />
    );
  }

  // Mirrors the Labels table loading pattern: initial-load renders a bare
  // spinner (no empty-state flash before the first fetch resolves), every
  // subsequent fetch renders the previous list with an overlay on top so the
  // content stays stable under the user's cursor.
  if (isInitialLoad) {
    return (
      <div className="card mb-2">
        <div className="card-body py-2">
          <small className="text-gray">Ideas for you</small>
          <div
            className="text-center my-4"
            data-testid="library-initial-spinner"
          >
            <FontAwesomeIcon icon="far fa-spinner" pulse size="lg" />
          </div>
        </div>
      </div>
    );
  }

  let emptyMessage = "No prompt templates available.";
  if (effectiveSearch) {
    emptyMessage = "No templates match your search.";
  } else if (categoryId) {
    emptyMessage =
      "No templates available for this category. Try selecting 'All' to browse all templates.";
  }

  const body =
    prompts.length === 0 ? (
      <div className="text-center my-4">
        <FontAwesomeIcon icon="far fa-frown" size="lg" />
        <div className="mt-2">{emptyMessage}</div>
      </div>
    ) : (
      <div className="list-group list-group-flush">
        {prompts.map((prompt) => (
          <button
            key={prompt.id}
            type="button"
            className="list-group-item list-group-item-action text-left"
            onClick={() => handlePromptClick(prompt)}
            title={prompt.body}
          >
            <strong className="d-block">{promptTitle(prompt)}</strong>
            {prompt.description && (
              <small className="text-secondary">{prompt.description}</small>
            )}
          </button>
        ))}
      </div>
    );

  return (
    <div className="card mb-2">
      <div className="card-body py-2">
        <small className="text-gray">Ideas for you</small>

        <div className="position-relative mt-2">
          {isLoading && (
            <div
              className="loading-overlay"
              data-testid="library-loading-overlay"
            >
              <FontAwesomeIcon icon="far fa-spinner" pulse className="fa-lg" />
            </div>
          )}
          {body}
        </div>

        {totalPages > 1 && !isLoading && (
          <ReactPaginate
            forcePage={currentPage - 1}
            pageCount={totalPages}
            pageRangeDisplayed={1}
            marginPagesDisplayed={2}
            containerClassName="pagination justify-content-end mt-2"
            activeClassName="active"
            breakLabel="..."
            breakLinkClassName="page-link disabled"
            breakClassName="page-item"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            disabledClassName="disabled"
            onPageChange={handlePageChange}
            previousLabel="Previous"
            nextLabel="Next"
          />
        )}
      </div>
    </div>
  );
};

PromptsList.propTypes = {
  handlePromptClick: PropTypes.func.isRequired,
  selectedCategory: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.string,
  }),
  onLoadingChange: PropTypes.func,
  moduleTag: PropTypes.string,
  channels: PropTypes.arrayOf(PropTypes.string),
  searchTerm: PropTypes.string,
};

export default PromptsList;
